import {
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  limit,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './firebase';

export type TeamActivityType =
  | 'team_created'
  | 'team_renamed'
  | 'member_added'
  | 'member_removed'
  | 'member_role_changed'
  | 'membership_type_changed'
  | 'description_changed'
  | 'settings_changed'
  | 'project_created';

export interface TeamActivity {
  id?: string;
  teamId: string;
  type: TeamActivityType;
  actorId: string;
  actorName?: string;
  metadata?: {
    fromValue?: any;
    toValue?: any;
    memberId?: string;
    memberName?: string;
    fromRole?: string;
    toRole?: string;
    [key: string]: any;
  };
  createdAt: Timestamp | ReturnType<typeof serverTimestamp>;
}

/**
 * Log a team activity event
 */
export async function logTeamActivity(
  orgId: string,
  activity: Omit<TeamActivity, 'id' | 'createdAt'>
): Promise<string> {
  // Remove undefined values - Firestore doesn't allow them
  const activityData: any = {
    teamId: activity.teamId,
    type: activity.type,
    actorId: activity.actorId,
    createdAt: serverTimestamp()
  };

  // Only add optional fields if they have defined values
  if (activity.actorName !== undefined) {
    activityData.actorName = activity.actorName;
  }
  if (activity.metadata !== undefined) {
    // Filter out undefined values from metadata to prevent Firestore errors
    const cleanMetadata: Record<string, any> = {};
    for (const [key, value] of Object.entries(activity.metadata)) {
      if (value !== undefined) {
        cleanMetadata[key] = value;
      }
    }
    // Only add metadata if it has any defined values
    if (Object.keys(cleanMetadata).length > 0) {
      activityData.metadata = cleanMetadata;
    }
  }

  const activityRef = await addDoc(
    collection(db, 'orgs', orgId, 'teams', activity.teamId, 'activities'),
    activityData
  );

  return activityRef.id;
}

/**
 * Get team activities in reverse chronological order
 */
export async function getTeamActivities(
  orgId: string,
  teamId: string,
  maxResults = 50
): Promise<TeamActivity[]> {
  const activitiesQuery = query(
    collection(db, 'orgs', orgId, 'teams', teamId, 'activities'),
    orderBy('createdAt', 'desc'),
    limit(maxResults)
  );

  const snapshot = await getDocs(activitiesQuery);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as TeamActivity[];
}

/**
 * Format activity for display
 */
export function formatTeamActivityMessage(activity: TeamActivity): string {
  const actor = activity.actorName || 'Someone';

  switch (activity.type) {
    case 'team_created':
      return `${actor} created the team`;

    case 'team_renamed':
      if (activity.metadata?.fromValue && activity.metadata?.toValue) {
        return `${actor} renamed the team from "${activity.metadata.fromValue}" to "${activity.metadata.toValue}"`;
      }
      return `${actor} renamed the team`;

    case 'member_added':
      return `${actor} added ${activity.metadata?.memberName || 'a member'}`;

    case 'member_removed':
      return `${actor} removed ${activity.metadata?.memberName || 'a member'}`;

    case 'member_role_changed':
      if (activity.metadata?.fromRole && activity.metadata?.toRole) {
        return `${actor} changed ${activity.metadata?.memberName || 'a member'}'s role from ${activity.metadata.fromRole} to ${activity.metadata.toRole}`;
      }
      return `${actor} changed ${activity.metadata?.memberName || 'a member'}'s role`;

    case 'membership_type_changed':
      if (activity.metadata?.fromValue && activity.metadata?.toValue) {
        return `${actor} changed membership type from ${activity.metadata.fromValue} to ${activity.metadata.toValue}`;
      }
      return `${actor} changed the membership type`;

    case 'description_changed':
      return `${actor} updated the description`;

    case 'settings_changed':
      return `${actor} updated team settings`;

    case 'project_created':
      if (activity.metadata?.projectName) {
        return `${actor} created project "${activity.metadata.projectName}"`;
      }
      return `${actor} created a project`;

    default:
      return `${actor} updated the team`;
  }
}
